import React, { useState, useEffect } from 'react'
import { useForm } from "react-hook-form"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { toAbsoluteUrl } from "../helpers"
import passwordResetService from "../../services/passwordReset.service"

function ResetPassword() {
    const {
        register,
        handleSubmit,
        watch,
        formState: { errors, isSubmitting },
    } = useForm()

    const [searchParams] = useSearchParams()
    const token = searchParams.get('token')
    const navigate = useNavigate()

    const [username, setUsername] = useState(null)
    const [message, setMessage] = useState(null)
    const [messageType, setMessageType] = useState('danger')
    const [isTokenValid, setIsTokenValid] = useState(null)
    const [isValidating, setIsValidating] = useState(true)
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)

    const newPassword = watch("newPassword")

    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setIsTokenValid(false)
                setMessage("This reset link is invalid or has expired. Please request a new one.")
                setIsValidating(false)
                return
            }

            try {
                const response = await passwordResetService.validateToken(token)
                if (response.data?.username) {
                    setUsername(response.data.username)
                    setIsTokenValid(true)
                } else {
                    setIsTokenValid(false)
                    setMessage("This reset link is invalid or has expired. Please request a new one.")
                }
            } catch (error) {
                setIsTokenValid(false)
                if (error.response?.status === 400 || error.response?.status === 404) {
                    setMessage("This reset link is invalid or has expired. Please request a new one.")
                } else {
                    setMessage("Something went wrong. Please try again later.")
                }
            } finally {
                setIsValidating(false)
            }
        }

        validateToken()
    }, [token])

    const validatePassword = (value) => {
        if (value.length < 8) {
            return "Password must be at least 8 characters and include a special character."
        }
        const specialCharRegex = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/
        if (!specialCharRegex.test(value)) {
            return "Password must be at least 8 characters and include a special character."
        }
        return true
    }

    const onSubmit = async (data) => {
        setMessage(null)

        if (data.newPassword !== data.confirmPassword) {
            setMessage("Passwords do not match. Please try again.")
            setMessageType('danger')
            return
        }

        try {
            await passwordResetService.resetPassword(token, data.newPassword)
            setIsSuccess(true)
            setMessage("Your password has been reset successfully!")
            setMessageType('success')

            setTimeout(() => {
                navigate('/login')
            }, 3000)
        } catch (error) {
            if (error.response?.status === 400 || error.response?.status === 404) {
                setMessage("This reset link is invalid or has expired. Please request a new one.")
            } else if (error.response?.data?.error) {
                setMessage(error.response.data.error)
            } else {
                setMessage("Something went wrong. Please try again later.")
            }
            setMessageType('danger')
        }
    }

    if (isValidating) {
        return (
            <div className="h-100 d-flex align-items-center justify-content-center py-5 min-vh-90">
                <div className="card shadow-lg w-100 w-md-500px">
                    <div className="card-body p-10 p-lg-15 text-center">
                        <div className="mb-10">
                            <img
                                src={toAbsoluteUrl('media/logos/eyeconx-logo.png')}
                                alt="Eyeconx Logo"
                                className="h-50px mb-5"
                                 height={"50px"}
                            />
                        </div>
                        <div className="d-flex justify-content-center align-items-center">
                            <span className="spinner-border spinner-border-lg text-primary me-3" role="status" aria-hidden="true"></span>
                            <span className="fs-5 text-gray-600">Validating reset link...</span>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (!isTokenValid) {
        return (
            <div className="h-100 d-flex align-items-center justify-content-center py-5 min-vh-90">
                <div className="card shadow-lg w-100 w-md-500px">
                    <div className="card-body p-10 p-lg-15">
                        <div className="text-center mb-10">
                            <img
                                src={toAbsoluteUrl('media/logos/eyeconx-logo.png')}
                                alt="Eyeconx Logo"
                                className="h-50px mb-5"
                                 height={"50px"}
                            />
                            <h1 className="text-dark fw-bolder mb-3">Reset Password</h1>
                        </div>

                        <div className="alert alert-danger d-flex align-items-center p-5 mb-10" role="alert">
                            <i className="bi bi-exclamation-triangle-fill me-3 fs-3" aria-hidden="true"></i>
                            <div>{message}</div>
                        </div>

                        <div className="text-center">
                            <Link to="/forgot-password" className="btn btn-primary me-3 text-white">
                                Request New Link
                            </Link>
                            <Link to="/login" className="btn btn-light">
                                Back to Login
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (isSuccess) {
        return (
            <div className="d-flex align-items-center justify-content-center min-vh-90 py-5">
                <div className="card shadow-lg w-100 w-md-500px">
                    <div className="card-body p-10 p-lg-15">
                        <div className="text-center mb-10">
                            <img
                                src={toAbsoluteUrl('media/logos/eyeconx-logo.png')}
                                alt="Eyeconx Logo"
                                className="h-50px mb-5"
                                 height={"50px"}
                            />
                            <h1 className="text-dark fw-bolder mb-3">Password Reset Successful</h1>
                        </div>

                        <div className="alert alert-success d-flex align-items-center p-5 mb-10" role="alert">
                            <i className="bi bi-check-circle-fill me-3 fs-3" aria-hidden="true"></i>
                            <div>
                                <p className="mb-1">{message}</p>
                                <p className="mb-0 text-muted small">Redirecting to login page...</p>
                            </div>
                        </div>

                        <div className="text-center">
                            <Link to="/login" className="btn btn-primary">
                                Go to Login
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="d-flex align-items-center justify-content-center min-vh-90 py-5">
            <div className="card shadow-lg w-100 w-md-500px">
                <div className="card-body p-10 p-lg-15">
                    <div className="text-center mb-10">
                        <img
                            src={toAbsoluteUrl('media/logos/eyeconx-logo.png')}
                            alt="Eyeconx Logo"
                            className="h-50px mb-5"
                             height={"50px"}
                        />
                        <h1 className="text-dark fw-bolder mb-3">Reset Password</h1>
                        {username && (
                            <div className="text-gray-500 fw-semibold fs-6">
                                Resetting password for: <span className="text-primary fw-bold">{username}</span>
                            </div>
                        )}
                    </div>

                    {message && (
                        <div className={`alert alert-${messageType} d-flex align-items-center p-5 mb-10`} role="alert">
                            <i className={`bi ${messageType === 'danger' ? 'bi-exclamation-triangle-fill' : 'bi-check-circle-fill'} me-3 fs-3`} aria-hidden="true"></i>
                            <div className="flex-grow-1">{message}</div>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={() => setMessage(null)}
                                aria-label="Close alert"
                            ></button>
                        </div>
                    )}

                    <form className="form w-100" onSubmit={handleSubmit(onSubmit)} noValidate>
                        <div className="fv-row mb-8">
                            <label htmlFor="newPassword" className="form-label fs-6 fw-bolder text-dark">
                                New Password
                            </label>
                            <div className="input-group">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    id="newPassword"
                                    className={`form-control form-control-lg form-control-solid ${errors.newPassword ? 'is-invalid' : ''}`}
                                    placeholder="Enter new password"
                                    aria-describedby="newPasswordHelp"
                                    {...register("newPassword", {
                                        required: "New password is required",
                                        validate: validatePassword
                                    })}
                                />
                                <button
                                    type="button"
                                    className="btn btn-outline-light text-muted"
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                >
                                    <i className={`bi bi-eye${showPassword ? '-slash' : ''}`} aria-hidden="true"></i>
                                </button>
                                {errors.newPassword && (
                                    <div className="invalid-feedback">{errors.newPassword.message}</div>
                                )}
                            </div>
                            <div id="newPasswordHelp" className="form-text text-muted small mt-2">
                                Password must be at least 8 characters and include a special character.
                            </div>
                        </div>

                        <div className="fv-row mb-8">
                            <label htmlFor="confirmPassword" className="form-label fs-6 fw-bolder text-dark">
                                Confirm New Password
                            </label>
                            <div className="input-group">
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    id="confirmPassword"
                                    className={`form-control form-control-lg form-control-solid ${errors.confirmPassword ? 'is-invalid' : ''}`}
                                    placeholder="Confirm new password"
                                    {...register("confirmPassword", {
                                        required: "Please confirm your password",
                                        validate: (value) =>
                                            value === newPassword || "Passwords do not match. Please try again."
                                    })}
                                />
                                <button
                                    type="button"
                                    className="btn btn-outline-light text-muted"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                                >
                                    <i className={`bi bi-eye${showConfirmPassword ? '-slash' : ''}`} aria-hidden="true"></i>
                                </button>
                                {errors.confirmPassword && (
                                    <div className="invalid-feedback">{errors.confirmPassword.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="d-flex flex-wrap justify-content-center pb-lg-0">
                            <button
                                type="submit"
                                className="btn btn-primary me-4 px-6 py-3"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <span className="d-flex align-items-center">
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Resetting...
                                    </span>
                                ) : (
                                    'Reset Password'
                                )}
                            </button>
                            <Link to="/login" className="btn btn-light px-6 py-3">
                                Cancel
                            </Link>
                        </div>
                    </form>

                    <div className="text-center mt-5">
                        <span className="text-gray-600 fs-6 fw-semibold">
                            Remember your password?{' '}
                            <Link to="/login" className="link-primary text-decoration-none">
                                Sign in
                            </Link>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ResetPassword
